import { useCallback, useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import classNames from 'classnames';
import { BlockContent } from 'components/blocks/BlockContent';
import { BoxWithShadow } from 'components/blocks/BoxWithShadow';
import { Layout } from 'components/resort/Layout/Layout';
import { Main } from 'components/resort/Layout/Main';
import WidgetTypeSelect, { WidgetTypeOption } from 'components/widget/WidgetTypeSelect';
import { Spinner } from 'components/Spinner';
import { WidgetSettingsState } from 'components/widget/WidgetSettings';
import {
  Nullable, GeneralWidgetPageProps, WidgetType, IframeEventMessage,
} from 'components/widget/types';
import { wrapServerSideProps } from 'util/wrapProps';
import { HeadMeta } from 'components/common/HeadMeta';
import { generateWidgetURLs, generateIframeTemplate, isSingleItemGuard } from 'components/widget/utils';
import { SearchData } from 'components/widget/WidgetSearch';
import { useOTSTranslation } from 'i18n/useOTSTranslation';

import widgetComponentsStyles from 'components/widget/styles.module.scss';
import styles from './styles.module.scss';

export const dynamicFallback = { loading: () => <Spinner /> };
const DynamicWidgetSearch = dynamic(() => import('components/widget/WidgetSearch'), dynamicFallback);
const DynamicWidgetSettings = dynamic(() => import('components/widget/WidgetSettings'), dynamicFallback);
const DynamicWidgetDemo = dynamic(() => import('components/widget/WidgetDemo'), dynamicFallback);
const DynamicWidgetGeneratorDisclaimer = dynamic(() => import('components/widget/WidgetGeneratorDisclaimer'), dynamicFallback);
const DynamicWidgetCode = dynamic(() => import('components/widget/WidgetCode'), dynamicFallback);

function WidgetGenerator() {
  const [widgetType, setWidgetType] = useState<Nullable<WidgetTypeOption>>(null);
  const [searchData, setSearchData] = useState<Nullable<SearchData>>(null);
  const [widgetSettings, setWidgetSetting] = useState<Nullable<WidgetSettingsState>>(null);
  const [widgetHeight, setWidgetHeight] = useState<number | undefined>(undefined);
  const [isAgreed, setAgreement] = useState<boolean>(false);
  const isSingleResort = widgetType?.value === WidgetType.SINGLE_RESORT;
  const isMultiSelect = widgetType?.value === WidgetType.LIST_OF_RESORTS;
  const isRegion = widgetType?.value === WidgetType.SINGLE_REGION;
  const { t: seoT } = useOTSTranslation('seo');
  const isMultipleChoice = searchData && !isSingleItemGuard(searchData);
  const showSettings = isMultipleChoice ? searchData.length !== 0 : !!searchData;

  const { url, poweredUrl } = generateWidgetURLs(
    widgetType?.value,
    searchData,
    widgetSettings?.filter?.value,
    widgetSettings?.language,
  );

  const iframeTemplate = generateIframeTemplate(
    widgetSettings?.width,
    widgetHeight,
    url,
    poweredUrl,
  );

  useEffect(() => {
    setSearchData(null);
    setWidgetSetting(null);
  }, [widgetType]);

  useEffect(() => {
    setWidgetHeight(undefined);
  }, [widgetType, widgetSettings, searchData]);

  const handleEvent = useCallback((e: MessageEvent<IframeEventMessage>) => {
    const value = e.data;
    if (
      ((e.origin.indexOf('skiinfo') > -1) || (e.origin.indexOf('onthesnow') > -1))
      && typeof value === 'string'
    ) {
      if (value) {
        setWidgetHeight(Number(value.replace('px', '')));
      }
    }
  }, [setWidgetHeight, widgetHeight]);

  useEffect(() => {
    window.addEventListener('message', handleEvent, false);

    return () => {
      window.removeEventListener('message', handleEvent);
    };
  }, []);

  return (
    <>
      <HeadMeta
        title={seoT('Other.skireport.title')}
        description={seoT('Other.skireport.description')}
      />
      <div className={classNames('container', styles.widgetGenerator)}>
        <Layout className={styles.widgetGeneratorLayout}>
          <Main>
            <BoxWithShadow>
              <BlockContent className={widgetComponentsStyles.widgetGeneratorContent}>
                <WidgetTypeSelect value={widgetType} setter={setWidgetType} className="mb-5" />
                {widgetType && (
                  <DynamicWidgetSearch
                    setter={setSearchData}
                    value={searchData}
                    isMulti={isMultiSelect}
                    isRegion={isRegion}
                    widgetType={widgetType.value}
                    className="mb-5"
                  />
                )}
                {showSettings && (
                  <DynamicWidgetSettings
                    setter={setWidgetSetting}
                    values={widgetSettings}
                    className="mb-5"
                    isRegion={isRegion}
                    isSingleResort={isSingleResort}
                  />
                )}
                {!!widgetSettings?.width && !!url && (
                  <DynamicWidgetDemo
                    setter={setWidgetSetting}
                    className="mb-5"
                    url={url}
                    poweredUrl={poweredUrl}
                    width={widgetSettings.width}
                    height={widgetHeight}
                  />
                )}
                {!!widgetHeight && <DynamicWidgetGeneratorDisclaimer className="mb-5" onAgree={setAgreement} />}
                {isAgreed && iframeTemplate && <DynamicWidgetCode template={iframeTemplate} />}
              </BlockContent>
            </BoxWithShadow>
          </Main>
        </Layout>
      </div>
    </>
  );
}

export default WidgetGenerator;

const fetchProps = async (): Promise<GeneralWidgetPageProps> => ({
  type: 'global',
  route: 'widget-generator',
  templateName: 'Widget Generator',
  templateId: '4.1.0',
  isWidgetPage: true,
});

export const getWidgetGeneratorServerSideProps = wrapServerSideProps(fetchProps);
